import React, { useMemo, useState } from 'react';
import { X, AlertCircle, Send } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import DateInput from '../ui/DateInput';
import {
  MEDIA_EVENT_CATEGORIES, MEDIA_EVENT_TONES, MEDIA_DURATION_OPTIONS, addHoursToLocal, localToIso,
} from '../../lib/media';

// MediaBookingForm — full questionnaire modal for /portal/media.
//
// Spec §6 fields:
//   eventTitle, scheduledStart/End, location + outsideNYC, primary +
//   backup contact, parking, description, category, priority shots, tone,
//   equipment notes, reference uploads (optional, deferred to MP.7 polish),
//   turnaround (Standard / Rush).
//
// Fee-acknowledgment gate: when the resulting notice tier or rush
// turnaround produces a fee, a checkbox must be ticked before submit
// (spec §7 + the bookings extension). The backend re-verifies, so a
// tampered payload still fails.

const CATEGORIES = MEDIA_EVENT_CATEGORIES;
const TONES = MEDIA_EVENT_TONES;

// Mirrors functions/_lib/media-booking.js — kept in sync as a UX hint;
// backend re-computes authoritatively.
function tierForDate(scheduledStartIso) {
  if (!scheduledStartIso) return null;
  const start = new Date(scheduledStartIso);
  if (Number.isNaN(start.valueOf())) return null;
  const localKey = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const startDay = new Date(localKey(start));
  const nowDay = new Date(localKey(new Date()));
  const days = Math.round((startDay - nowDay) / 86_400_000);
  if (days >= 14) return 'standard';
  if (days >= 7) return 'rush';
  return 'emergency';
}


const initial = {
  eventTitle: '',
  scheduledStartAt: '',
  scheduledEndAt: '',
  locationAddress: '',
  locationVenueName: '',
  locationOutsideNyc: false,
  primaryContactName: '',
  primaryContactPhone: '',
  primaryContactRole: '',
  backupContactName: '',
  backupContactPhone: '',
  parkingInstructions: '',
  eventDescription: '',
  eventCategory: '',
  priorityShots: '',
  tone: '',
  equipmentNotes: '',
  turnaround: 'standard',
  durationHours: 2,
};

// Pretty-print a "YYYY-MM-DDTHH:MM" local string for the "Ends …" hint.
function fmtEnd(localStr) {
  const d = new Date(localStr);
  if (Number.isNaN(d.valueOf())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const DURATION_OPTIONS = MEDIA_DURATION_OPTIONS;

export default function MediaBookingForm({ pkg, onClose, onBooked, initialStartAt }) {
  const [form, setForm] = useState(() => {
    // F1 — when launched from a calendar slot, prefill the date (start + a
    // 2-hour default window) so the school doesn't re-type it.
    if (!initialStartAt) return initial;
    const start = new Date(initialStartAt);
    if (Number.isNaN(start.valueOf())) return initial;
    const pad = (n) => String(n).padStart(2, '0');
    const startLocal = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
    return { ...initial, scheduledStartAt: startLocal, scheduledEndAt: addHoursToLocal(startLocal, initial.durationHours) };
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const tier = useMemo(() => tierForDate(form.scheduledStartAt), [form.scheduledStartAt]);
  const shortNotice = tier && tier !== 'standard';
  const submitDisabled = submitting;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // Start + duration drive the (hidden) end time, so coverage can never exceed
  // the 4-hour cap the way a free end-time picker allowed.
  const setStart = (v) => setForm((p) => ({ ...p, scheduledStartAt: v, scheduledEndAt: addHoursToLocal(v, p.durationHours) }));
  const setDuration = (h) => setForm((p) => ({ ...p, durationHours: h, scheduledEndAt: addHoursToLocal(p.scheduledStartAt, h) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setSubmitting(true);
    setError('');
    setFieldErrors({});
    try {
      // The date inputs are zone-less browser-local strings; send real UTC
      // instants so the server never has to guess the school's timezone.
      const body = {
        ...form,
        scheduledStartAt: localToIso(form.scheduledStartAt) || form.scheduledStartAt,
        scheduledEndAt: localToIso(form.scheduledEndAt) || form.scheduledEndAt,
      };
      const r = await api.post('/api/client/media/events', body);
      onBooked?.(r);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data?.fieldErrors) setFieldErrors(err.data.fieldErrors);
        setError(err.message || 'Failed to submit booking');
      } else {
        setError('Network error, please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-2xl bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-lg font-semibold text-white tracking-tight">Book an event</h2>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {pkg.eventsRemaining} event{pkg.eventsRemaining === 1 ? '' : 's'} remaining on your {pkg.label}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-5 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <Field label="Event title" required err={fieldErrors.eventTitle}>
            <input type="text" required value={form.eventTitle} onChange={(e) => set('eventTitle', e.target.value)}
              placeholder="8th Grade Graduation"
              className={inputCls(!!fieldErrors.eventTitle)} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Start date & time" required err={fieldErrors.scheduledStartAt}>
              <DateInput withTime required value={form.scheduledStartAt}
                onChange={setStart}
                className={inputCls(!!fieldErrors.scheduledStartAt)} />
            </Field>
            <Field label="Duration" required err={fieldErrors.scheduledEndAt}>
              <select value={form.durationHours} onChange={(e) => setDuration(Number(e.target.value))}
                className={inputCls(!!fieldErrors.scheduledEndAt)}>
                {DURATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {form.scheduledStartAt && form.scheduledEndAt && (
                <span className="block text-[11px] text-gray-500 mt-1">
                  Ends {fmtEnd(form.scheduledEndAt)} · coverage capped at 4 hours.
                </span>
              )}
            </Field>
          </div>

          {shortNotice && (
            <div className="px-3 py-2.5 rounded-lg border border-amber-400/30 bg-amber-500/[0.04] flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
              <div className="text-[12px] text-amber-100/90">
                This date is on short notice. NexSav will review availability before confirming.
              </div>
            </div>
          )}

          <Field label="Location address" required err={fieldErrors.locationAddress}>
            <input type="text" required value={form.locationAddress} onChange={(e) => set('locationAddress', e.target.value)}
              placeholder="Full street address"
              className={inputCls(!!fieldErrors.locationAddress)} />
          </Field>
          <Field label="Venue name (optional)">
            <input type="text" value={form.locationVenueName} onChange={(e) => set('locationVenueName', e.target.value)}
              placeholder="School auditorium, gym, etc." className={inputCls(false)} />
          </Field>
          <label className="inline-flex items-start gap-2 text-[12px] text-gray-400 cursor-pointer">
            <input type="checkbox" checked={form.locationOutsideNyc}
              onChange={(e) => set('locationOutsideNyc', e.target.checked)}
              className="mt-0.5 w-3.5 h-3.5 rounded accent-[#B0FC38]" />
            <span>
              Event is <strong className="text-gray-300">outside the five boroughs of NYC</strong>.
              NexSav will review travel feasibility before confirming.
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Primary contact name" required err={fieldErrors.primaryContactName}>
              <input type="text" required value={form.primaryContactName}
                onChange={(e) => set('primaryContactName', e.target.value)}
                placeholder="Who CJ asks for on arrival" className={inputCls(!!fieldErrors.primaryContactName)} />
            </Field>
            <Field label="Primary contact phone" required err={fieldErrors.primaryContactPhone}>
              <input type="tel" required value={form.primaryContactPhone}
                onChange={(e) => set('primaryContactPhone', e.target.value)}
                placeholder="212-555-0100" className={inputCls(!!fieldErrors.primaryContactPhone)} />
            </Field>
          </div>
          <Field label="Primary contact role (optional)">
            <input type="text" value={form.primaryContactRole} onChange={(e) => set('primaryContactRole', e.target.value)}
              placeholder="Principal, parent coordinator, etc." className={inputCls(false)} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Backup contact name (optional)">
              <input type="text" value={form.backupContactName} onChange={(e) => set('backupContactName', e.target.value)}
                className={inputCls(false)} />
            </Field>
            <Field label="Backup contact phone (optional)">
              <input type="tel" value={form.backupContactPhone} onChange={(e) => set('backupContactPhone', e.target.value)}
                className={inputCls(false)} />
            </Field>
          </div>

          <Field label="Parking / arrival instructions">
            <textarea rows="2" value={form.parkingInstructions} onChange={(e) => set('parkingInstructions', e.target.value)}
              placeholder="Where to park, which entrance to use" className={inputCls(false)} />
          </Field>
          <Field label="Event description">
            <textarea rows="3" value={form.eventDescription} onChange={(e) => set('eventDescription', e.target.value)}
              placeholder="One paragraph: what's happening, attendance, focus moments." className={inputCls(false)} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.eventCategory} onChange={(e) => set('eventCategory', e.target.value)}
                className={inputCls(false)}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Tone">
              <select value={form.tone} onChange={(e) => set('tone', e.target.value)}
                className={inputCls(false)}>
                {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Priority shots (1–2 highlights you care most about)">
            <input type="text" value={form.priorityShots} onChange={(e) => set('priorityShots', e.target.value)}
              placeholder="Principal's speech, student walk, etc." className={inputCls(false)} />
          </Field>
          <Field label="Equipment notes (optional)">
            <input type="text" value={form.equipmentNotes} onChange={(e) => set('equipmentNotes', e.target.value)}
              placeholder="Aerial shots, low-light, etc." className={inputCls(false)} />
          </Field>

          <div className="px-3 py-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] text-[12px] text-gray-400">
            Delivery turnaround: <strong className="text-gray-200">10 business days</strong> after your event.
            Every booking is reviewed by the NexSav team before it's confirmed. You'll get an email once it's approved and scheduled.
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-[13px] text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={submitDisabled}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 disabled:cursor-not-allowed">
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting…' : 'Submit booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, err, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">
        {label}{required && <span className="text-amber-300 ml-1">*</span>}
      </span>
      {children}
      {err && <span className="block text-[11px] text-red-300 mt-1">{err}</span>}
    </label>
  );
}

function inputCls(hasError) {
  return `w-full px-3 py-2 rounded-lg bg-white/[0.04] border ${hasError ? 'border-red-500/50' : 'border-white/[0.08]'} text-white text-[13px] placeholder-gray-600 focus:outline-none focus:border-[#B0FC38]/40 focus:bg-white/[0.06]`;
}
